import type { OrgLocationDto, ProjectLocationDto } from '../dtos/OrgLocationDto.js';
import { getSupabaseClient } from '@/core/supabase/client.js';
import { fetchAllPaged } from '@/core/supabase/pagination.js';
import type {
  LocationImportRow,
  ProjectLocationImportPlan,
} from '../../domain/repositories/IOrgProjectLocationRepository.js';

const ORG_LOC_FIELDS = `id, org_id, location_id, custom_code, custom_category, metadata, created_at, updated_at, created_by`;
const PROJ_LOC_FIELDS = `id, org_id, project_id, location_id, custom_code, custom_category, working_shifts, working_days, geo_fence_radius_m, notes, metadata, created_at, updated_at, created_by`;

async function functionErrorMessage(error: any, operation: string): Promise<string> {
  const response = error?.context;
  let detail = "";
  if (response && typeof response.clone === "function") {
    try {
      const body = await response.clone().json();
      detail = body?.error ?? body?.message ?? "";
    } catch {
      try {
        detail = (await response.clone().text()).trim();
      } catch {
        // Keep the SDK error below when the response body is unavailable.
      }
    }
  }
  const status = typeof response?.status === "number" ? ` (${response.status})` : "";
  return `${operation} failed${status}: ${detail || error?.message || "Unknown Edge Function error"}`;
}

export class OrgProjectLocationDataSource {
  private get supabase() { return getSupabaseClient() as any; }

  async listByOrg(orgId: string): Promise<OrgLocationDto[]> {
    return fetchAllPaged<OrgLocationDto>((from, to) =>
      this.supabase
        .from('org_locations')
        .select(ORG_LOC_FIELDS)
        .eq('org_id', orgId)
        .order('created_at')
        .order('id')
        .range(from, to),
    );
  }

  async getByOrgLocation(orgId: string, locationId: string): Promise<OrgLocationDto | null> {
    const { data, error } = await this.supabase
      .from('org_locations')
      .select(ORG_LOC_FIELDS)
      .eq('org_id', orgId)
      .eq('location_id', locationId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async ensureOrgSubscription(orgId: string, locationId: string): Promise<void> {
    const { data: { user } } = await this.supabase.auth.getUser();
    const { error } = await this.supabase
      .from('org_locations')
      .upsert(
        { org_id: orgId, location_id: locationId, created_by: user?.id },
        { onConflict: 'org_id,location_id', ignoreDuplicates: true }
      );
    if (error) throw new Error(error.message);
  }

  async subscribeOrg(payload: {
    org_id: string;
    location_id: string;
    custom_code?: string | null;
    custom_category?: string | null;
  }): Promise<OrgLocationDto> {
    const { data: { user } } = await this.supabase.auth.getUser();
    const { data: inserted, error } = await this.supabase
      .from('org_locations')
      .upsert(
        { ...payload, created_by: user?.id },
        { onConflict: 'org_id,location_id', ignoreDuplicates: true }
      )
      .select(ORG_LOC_FIELDS)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (inserted) return inserted;
    const { data, error: fetchErr } = await this.supabase
      .from('org_locations')
      .select(ORG_LOC_FIELDS)
      .eq('org_id', payload.org_id)
      .eq('location_id', payload.location_id)
      .single();
    if (fetchErr) throw new Error(fetchErr.message);
    return data;
  }

  async listByProject(projectId: string): Promise<ProjectLocationDto[]> {
    return fetchAllPaged<ProjectLocationDto>((from, to) =>
      this.supabase
        .from('project_locations')
        .select(PROJ_LOC_FIELDS)
        .eq('project_id', projectId)
        .order('created_at')
        .order('id')
        .range(from, to),
    );
  }

  async getByProjectLocation(projectId: string, locationId: string): Promise<ProjectLocationDto | null> {
    const { data, error } = await this.supabase
      .from('project_locations')
      .select(PROJ_LOC_FIELDS)
      .eq('project_id', projectId)
      .eq('location_id', locationId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async hasProjectSubscriptions(orgId: string, locationId: string): Promise<boolean> {
    const { count, error } = await this.supabase
      .from('project_locations')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', orgId)
      .eq('location_id', locationId);
    if (error) throw new Error(error.message);
    return (count ?? 0) > 0;
  }

  async updateOrgLocation(
    orgId: string,
    locationId: string,
    patch: { customCode?: string | null },
  ): Promise<OrgLocationDto> {
    const { data, error } = await this.supabase
      .from('org_locations')
      .update({ custom_code: patch.customCode ?? null, updated_at: new Date().toISOString() })
      .eq('org_id', orgId)
      .eq('location_id', locationId)
      .select(ORG_LOC_FIELDS)
      .single();
    if (error) throw new Error(error.message);
    return data;
  }

  async unsubscribeOrg(orgId: string, locationId: string): Promise<void> {
    const { error } = await this.supabase
      .from('org_locations')
      .delete()
      .eq('org_id', orgId)
      .eq('location_id', locationId);
    if (error) throw new Error(error.message);
  }

  async subscribeProject(payload: {
    org_id: string;
    project_id: string;
    location_id: string;
    custom_code?: string | null;
    custom_category?: string | null;
    geo_fence_radius_m?: number;
    metadata?: Record<string, unknown>;
  }): Promise<ProjectLocationDto> {
    const { data: { user } } = await this.supabase.auth.getUser();
    const { data: inserted, error } = await this.supabase
      .from('project_locations')
      .upsert(
        { ...payload, created_by: user?.id },
        { onConflict: 'project_id,location_id', ignoreDuplicates: true }
      )
      .select(PROJ_LOC_FIELDS)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (inserted) return inserted;
    const { data, error: fetchErr } = await this.supabase
      .from('project_locations')
      .select(PROJ_LOC_FIELDS)
      .eq('project_id', payload.project_id)
      .eq('location_id', payload.location_id)
      .single();
    if (fetchErr) throw new Error(fetchErr.message);
    return data;
  }

  async updateProjectLocation(
    projectId: string,
    locationId: string,
    patch: { customCode?: string | null },
  ): Promise<ProjectLocationDto> {
    const { data, error } = await this.supabase
      .from('project_locations')
      .update({ custom_code: patch.customCode ?? null, updated_at: new Date().toISOString() })
      .eq('project_id', projectId)
      .eq('location_id', locationId)
      .select(PROJ_LOC_FIELDS)
      .single();
    if (error) throw new Error(error.message);
    return data;
  }

  async unsubscribeProject(projectId: string, locationId: string): Promise<void> {
    const { error } = await this.supabase
      .from('project_locations')
      .delete()
      .eq('project_id', projectId)
      .eq('location_id', locationId);
    if (error) throw new Error(error.message);
  }

  async exportByProject(options: {
    projectId: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<(ProjectLocationDto & { location: { id: string; name: string; code: string; address_line: string | null; full_address_cache: string | null; province_code: string | null; latitude: number | null; longitude: number | null } | null })[]> {
    type ExportRow = ProjectLocationDto & { location: { id: string; name: string; code: string; address_line: string | null; full_address_cache: string | null; province_code: string | null; latitude: number | null; longitude: number | null } | null };
    return fetchAllPaged<ExportRow>((from, to) => {
      let query = this.supabase
        .from('project_locations')
        .select(`${PROJ_LOC_FIELDS}, location:locations!location_id(id, name, code, address_line, full_address_cache, province_code, latitude, longitude)`)
        .eq('project_id', options.projectId)
        .order('created_at', { ascending: true })
        .order('id')
        .range(from, to);
      if (options.dateFrom) query = query.gte('created_at', options.dateFrom);
      if (options.dateTo) query = query.lte('created_at', options.dateTo);
      return query;
    });
  }

  async listByProjectCustomCodes(
    projectId: string,
    codes: string[],
  ): Promise<{ custom_code: string; location_id: string }[]> {
    if (!codes.length) return [];
    const { data, error } = await this.supabase
      .from('project_locations')
      .select('custom_code, location_id')
      .eq('project_id', projectId)
      .in('custom_code', codes);
    if (error) throw new Error(error.message);
    return data ?? [];
  }

  async importProjectSchedule(
    orgId: string,
    projectId: string,
    rows: Array<Record<string, unknown>>,
  ): Promise<Array<{ key: string; ok: boolean; location_id?: string; session_id?: string; error?: string }>> {
    const { data, error } = await this.supabase.rpc('import_project_schedule', {
      p_org_id: orgId,
      p_project_id: projectId,
      p_rows: rows,
    });
    if (error) throw new Error(error.message);
    return (data ?? []) as Array<{ key: string; ok: boolean; location_id?: string; session_id?: string; error?: string }>;
  }

  async previewProjectLocationImport(
    orgId: string,
    projectId: string,
    rows: Array<Record<string, unknown>>,
  ): Promise<ProjectLocationImportPlan> {
    const { data, error } = await this.supabase.functions.invoke('location-import-coordinator', {
      body: { action: 'preview', orgId, projectId, rows },
    });
    if (error) throw new Error(await functionErrorMessage(error, 'Previewing project location import'));
    return data as ProjectLocationImportPlan;
  }

  async commitQueuedProjectLocationImport(orgId: string, projectId: string, importId: string): Promise<void> {
    const { error } = await this.supabase.functions.invoke('location-import-coordinator', {
      body: { action: 'commit', orgId, projectId, importId },
    });
    if (error) throw new Error(await functionErrorMessage(error, 'Committing location import'));
  }

  async queueProjectLocationImport(
    orgId: string,
    projectId: string,
    rows: LocationImportRow[],
  ): Promise<{ importId: string }> {
    const { data: created, error: createError } = await this.supabase.functions.invoke('location-import-coordinator', {
      body: { action: 'create', orgId, projectId },
    });
    if (createError) throw new Error(await functionErrorMessage(createError, 'Creating location import queue'));
    if (!created?.importId || !created?.path || !created?.token) throw new Error('Creating location import queue failed: invalid coordinator response');

    const manifest = new Blob([JSON.stringify({ rows })], { type: 'application/json' });
    const { error: uploadError } = await this.supabase.storage
      .from('location-imports')
      .uploadToSignedUrl(created.path, created.token, manifest, { contentType: 'application/json' });
    if (uploadError) throw new Error(uploadError.message);

    const { data: staged, error: stageError } = await this.supabase.functions.invoke('location-import-coordinator', {
      body: { action: 'stage', orgId, projectId, importId: created.importId },
    });
    if (stageError) throw new Error(await functionErrorMessage(stageError, 'Validating location import'));
    if (!staged?.staged) throw new Error(staged?.error ?? 'Staging location import failed');
    return { importId: created.importId };
  }

  async getQueuedProjectLocationImportStatus(orgId: string, projectId: string, importId: string): Promise<Record<string, unknown>> {
    const { data, error } = await this.supabase.functions.invoke('location-import-coordinator', {
      body: { action: 'status', orgId, projectId, importId },
    });
    if (error) throw new Error(await functionErrorMessage(error, 'Reading location import status'));
    return data;
  }

  async resumeQueuedProjectLocationImport(orgId: string, projectId: string, importId: string): Promise<void> {
    const { error } = await this.supabase.functions.invoke('location-import-coordinator', {
      body: { action: 'resume', orgId, projectId, importId },
    });
    if (error) throw new Error(await functionErrorMessage(error, 'Resuming location import'));
  }

  async confirmQueuedProjectLocationImportDuplicates(
    orgId: string,
    projectId: string,
    importId: string,
    rowNumbers: number[],
  ): Promise<void> {
    const { error } = await this.supabase.functions.invoke('location-import-coordinator', {
      body: { action: 'confirm', orgId, projectId, importId, rowNumbers },
    });
    if (error) throw new Error(await functionErrorMessage(error, 'Confirming duplicate location warning'));
  }

  async useQueuedProjectLocationImportDuplicates(
    orgId: string,
    projectId: string,
    importId: string,
    rowNumbers: number[],
  ): Promise<{ alreadySubscribed: number }> {
    const { data, error } = await this.supabase.functions.invoke('location-import-coordinator', {
      body: { action: 'use_existing', orgId, projectId, importId, rowNumbers },
    });
    if (error) throw new Error(await functionErrorMessage(error, 'Using existing duplicate locations'));
    return { alreadySubscribed: Number(data?.alreadySubscribed ?? 0) };
  }

  async importProjectLocations(
    orgId: string,
    projectId: string,
    rows: Array<Record<string, unknown>>,
  ): Promise<Array<{ key: string; ok: boolean; action?: string; location_id?: string; error?: string }>> {
    const { data, error } = await this.supabase.rpc('import_project_locations', {
      p_org_id: orgId,
      p_project_id: projectId,
      p_rows: rows,
    });
    if (error) throw new Error(error.message);
    return (data ?? []) as Array<{ key: string; ok: boolean; action?: string; location_id?: string; error?: string }>;
  }
}
